/**
 * Wiki links (SPEC §8.3). In titles a link is the token `[[id|Title]]`; in bodies it is
 * a `wikilink` node with {id, title}. Rendering always shows the target's current title.
 */
import * as Y from 'yjs'

export const LINK_TOKEN_RE = /\[\[([^\]|]+)\|([^\]]*)\]\]/g

export interface TitleSegment {
  kind: 'text' | 'link' | 'url'
  text: string
  id?: string
  url?: string
}

export function makeLinkToken(id: string, title: string): string {
  return `[[${id}|${title.replace(/[\[\]|]/g, ' ').trim()}]]`
}

export function titleLinkIds(title: string): string[] {
  const out: string[] = []
  for (const m of title.matchAll(LINK_TOKEN_RE)) out.push(m[1]!)
  return out
}

const URL_RE = /\bhttps?:\/\/[^\s<>"')\]]+/g

/** Split a stored title into text / link / url segments for display. */
export function parseTitle(title: string, titleOf?: (id: string) => string | undefined): TitleSegment[] {
  const segs: TitleSegment[] = []
  let last = 0
  const pushText = (text: string) => {
    if (!text) return
    let l = 0
    for (const m of text.matchAll(URL_RE)) {
      if (m.index! > l) segs.push({ kind: 'text', text: text.slice(l, m.index) })
      segs.push({ kind: 'url', text: m[0], url: m[0] })
      l = m.index! + m[0].length
    }
    if (l < text.length) segs.push({ kind: 'text', text: text.slice(l) })
  }
  for (const m of title.matchAll(LINK_TOKEN_RE)) {
    pushText(title.slice(last, m.index))
    const id = m[1]!
    segs.push({ kind: 'link', id, text: titleOf?.(id) ?? m[2]! })
    last = m.index! + m[0].length
  }
  pushText(title.slice(last))
  return segs
}

/** Title as plain text with links replaced by the target's current title. */
export function displayTitle(title: string, titleOf?: (id: string) => string | undefined): string {
  if (!title.includes('[[')) return title
  return title.replace(LINK_TOKEN_RE, (_m, id: string, t: string) => titleOf?.(id) ?? t)
}

/**
 * Editable form of a stored title: `[[id|Title]]` → `[[Title]]` (current title).
 * Returns the display string plus the ordered link ids so edits can be mapped back.
 */
export function toEditable(stored: string, titleOf?: (id: string) => string | undefined): { text: string; ids: string[] } {
  const ids: string[] = []
  const text = stored.replace(LINK_TOKEN_RE, (_m, id: string, t: string) => {
    ids.push(id)
    return `[[${(titleOf?.(id) ?? t).replace(/[\[\]|]/g, ' ')}]]`
  })
  return { text, ids }
}

/**
 * Map an edited display string back to storage. Each `[[Label]]` is matched to the
 * previous ids in order when its label still equals that target's title; otherwise it
 * is resolved by exact title (`resolve`) or left as plain `[[Label]]` text.
 */
export function fromEditable(
  edited: string,
  prevIds: string[],
  titleOf: (id: string) => string | undefined,
  resolve?: (label: string) => string | undefined,
): string {
  let cursor = 0
  return edited.replace(/\[\[([^\]|]+)\]\]/g, (m, label: string) => {
    const want = label.trim()
    for (let i = cursor; i < prevIds.length; i++) {
      const id = prevIds[i]!
      const t = (titleOf(id) ?? '').replace(/[\[\]|]/g, ' ').trim()
      if (t === want) {
        cursor = i + 1
        return makeLinkToken(id, titleOf(id) ?? want)
      }
    }
    const rid = resolve?.(want)
    return rid ? makeLinkToken(rid, want) : m
  })
}

/** Link targets inside a rich body fragment. */
export function bodyLinkIds(frag: Y.XmlFragment | undefined): string[] {
  if (!frag || frag.length === 0) return []
  const out: string[] = []
  const walk = (n: unknown) => {
    if (n instanceof Y.XmlElement) {
      if (n.nodeName === 'wikilink') {
        const id = n.getAttribute('id')
        if (typeof id === 'string') out.push(id)
        return
      }
      for (const c of n.toArray()) walk(c)
    } else if (n instanceof Y.XmlFragment) {
      for (const c of n.toArray()) walk(c)
    }
  }
  walk(frag)
  return out
}

/** Backlinks index: target id → source ids (computed on the client from titles + bodies). */
export function buildBacklinks(sources: Iterable<{ id: string; linkIds: string[] }>): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>()
  for (const s of sources) {
    for (const t of s.linkIds) {
      if (t === s.id) continue
      let set = out.get(t)
      if (!set) out.set(t, (set = new Set()))
      set.add(s.id)
    }
  }
  return out
}

export function extractUrls(text: string): string[] {
  return [...text.matchAll(URL_RE)].map((m) => m[0])
}

/** Is the whole string a single URL (pasting a link into a row → link item)? */
export function isSingleUrl(text: string): boolean {
  const t = text.trim()
  return /^https?:\/\/\S+$/.test(t)
}
